// ClusterRunnerPage — rendert één cluster: alle secties + items + nav-buttons.
// Harde gate: "Afronden cluster" alleen als sectionComplete() voor alle secties.
// Soft-warnings zijn geen blokkade — gewone warning + "Toevoegen aan risicoregister".

import React, { useEffect, useMemo } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    Stack,
    Typography,
    Tooltip,
    Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SectionForm from '../clusters/SectionForm.jsx';
import ProgressStepper from '../components/ProgressStepper.jsx';
import { useWizardContext } from './WizardPage.jsx';
import { sectionComplete, clusterComplete } from '../hooks/useSurvey.js';
import VariantBar from '../clusters/VariantBar.jsx';
import { variantKey, variantState } from '../variants.js';

export default function ClusterRunnerPage() {
    const { clusterId, projectId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const focusItemId = searchParams.get('focus');
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { config, project, survey, risks, addRisk, reloadRisks, updateVariants } = useWizardContext();

    // Scroll + highlight bij ?focus=<itemId>
    useEffect(() => {
        if (!focusItemId) return;
        let cleared = false;
        const timer = setTimeout(() => {
            const el = document.getElementById(`item-${focusItemId}`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.style.transition = 'background-color 0.3s';
                el.style.backgroundColor = 'rgba(230,142,45,0.18)';
                setTimeout(() => { el.style.backgroundColor = ''; }, 2600);
            }
            if (!cleared) {
                cleared = true;
                setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('focus');
                    return next;
                }, { replace: true });
            }
        }, 180);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusItemId, clusterId]);

    const cluster = useMemo(
        () => (config?.clusters || []).find(c => c.id === clusterId),
        [config, clusterId]
    );
    const clusters = config?.clusters || [];
    const idx = clusters.findIndex(c => c.id === clusterId);
    const prevCluster = idx > 0 ? clusters[idx - 1] : null;
    const nextCluster = idx >= 0 && idx < clusters.length - 1 ? clusters[idx + 1] : null;

    // Opties: welke optie staat open (?optie=2), en welke is de voorkeur.
    const variants = variantState(project, cluster);
    const requested = parseInt(searchParams.get('optie'), 10);
    const variant = variants
        ? (requested >= 1 && requested <= variants.count ? requested : variants.preferred)
        : 1;
    const selectVariant = (v) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('optie', String(v));
            return next;
        }, { replace: true });
    };

    // "Afronden" kijkt naar de voorkeursoptie; de sectie-vinkjes naar de optie die open staat.
    const complete = clusterComplete(cluster, survey.data?.[clusterId], variants?.preferred || 1);
    const allSurveyData = survey.data;

    if (!cluster) {
        return (
            <Alert severity="error">Cluster onbekend: {clusterId}</Alert>
        );
    }

    const sectionStatuses = cluster.sections.map(s => ({
        id: s.id,
        complete: sectionComplete(s, survey.data?.[clusterId]?.[variantKey(s.id, variant)]),
    }));

    const goPrev = () => {
        if (prevCluster) {
            navigate(`/projects/${projectId}/wizard/${prevCluster.id}`);
        } else {
            navigate(`/projects/${projectId}/wizard`);
        }
    };

    const goNext = async () => {
        // Eerst opslaan, dan hervalideren, zodat het risicoregister klopt.
        await survey.flush();
        await reloadRisks();
        if (nextCluster) {
            navigate(`/projects/${projectId}/wizard/${nextCluster.id}`);
        } else {
            navigate(`/projects/${projectId}/report`);
        }
    };

    const syncIndicator = (() => {
        if (survey.syncState === 'error') { return { label: t('wizard.saveError'), color: 'warning' }; }
        if (survey.syncState === 'saving' || survey.pendingCount > 0) { return { label: t('wizard.saving'), color: 'default' }; }
        return { label: t('wizard.savedServer'), color: 'success' };
    })();

    return (
        <Box>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ mb: 2 }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
            >
                <Box>
                    <Typography variant="overline" color="text.secondary">
                        {t('wizard.cluster')} {cluster.id}
                    </Typography>
                    <Typography variant="h1">{cluster.title}</Typography>
                    {cluster.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 760 }}>
                            {cluster.description}
                        </Typography>
                    )}
                </Box>
                <Chip
                    size="small"
                    color={syncIndicator.color}
                    variant={syncIndicator.color === 'default' ? 'outlined' : 'filled'}
                    label={syncIndicator.label}
                />
            </Stack>

            <ProgressStepper
                clusters={clusters}
                currentClusterId={clusterId}
                projectId={projectId}
                progress={survey.progress}
            />

            <Divider sx={{ mb: 2 }} />

            {variants && (
                <VariantBar
                    state={variants}
                    current={variant}
                    onSelect={selectVariant}
                    onUpdate={(body) => updateVariants(clusterId, body)}
                />
            )}

            {cluster.sections.map(section => (
                <SectionForm
                    key={`${section.id}@${variant}`}
                    variant={variant}
                    section={section}
                    clusterId={clusterId}
                    getItem={survey.getItem}
                    setItem={survey.setItem}
                    projectId={projectId}
                    allSurveyData={allSurveyData}
                    onAddToRegister={addRisk}
                    riskRegister={risks}
                />
            ))}

            <Divider sx={{ my: 3 }} />

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
            >
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={goPrev}
                    size="large"
                    variant="outlined"
                >
                    {prevCluster ? `${t('common.previous')}: ${prevCluster.id}` : t('common.back')}
                </Button>

                <Stack direction="row" spacing={1} alignItems="center">
                    {sectionStatuses.map(s => (
                        <Chip
                            key={s.id}
                            size="small"
                            label={s.id}
                            color={s.complete ? 'success' : 'default'}
                            variant={s.complete ? 'filled' : 'outlined'}
                            icon={s.complete ? <CheckIcon /> : undefined}
                        />
                    ))}
                </Stack>

                <Tooltip title={complete ? '' : t('wizard.completeClusterLocked')}>
                    <span>
                        <Button
                            variant="contained"
                            size="large"
                            disabled={!complete}
                            endIcon={<ArrowForwardIcon />}
                            onClick={goNext}
                        >
                            {nextCluster
                                ? `${t('wizard.completeCluster')} → ${nextCluster.id}`
                                : t('wizard.completeCluster')}
                        </Button>
                    </span>
                </Tooltip>
            </Stack>

            <Snackbar
                open={survey.syncState === 'error'}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert severity="warning">
                    {survey.lastError?.statusCode && survey.lastError.statusCode < 500
                        ? `Opslaan geweigerd: ${survey.lastError.message}`
                        : 'Geen verbinding met de server. Je invoer staat veilig op dit apparaat en wordt automatisch verstuurd.'}
                </Alert>
            </Snackbar>
        </Box>
    );
}
